<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch, onUnmounted } from 'vue';
import { useEventListener } from '@vueuse/core';

import { CategoryType } from '@libs/trip-planner-interfaces';
import type {
  AutoCompleteSuggestion,
  AutoCompleteSuggestionsByCategory,
} from '@libs/trip-planner-interfaces';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import { AppFadeTransition } from '../transitions';
import { KeyboardKeyCode } from '../../models';
import AppHighlighter from '../appHighlighter/AppHighlighter.vue';
import type { AppSvgIconType } from '../appSvgIcon/AppSvgIcon.model';

export interface AppAutosuggestDropdownProps {
  suggestions?: AutoCompleteSuggestionsByCategory[];
  selected?: string;
  maxVisibleItems?: number;
  wrapperBottomBoundary?: number;
  highlight?: boolean;
  headerIcon?: boolean;
  isOpen?: boolean;
}

const props = withDefaults(defineProps<AppAutosuggestDropdownProps>(), {
  suggestions: () => [],
  isOpen: false,
  selected: '',
  maxVisibleItems: 0,
});

interface AppAutosuggestDropdownEmits {
  (eventName: 'select', value: AutoCompleteSuggestion): void;
  (eventName: 'close'): void;
  (eventName: 'update-should-open-above', value: boolean): void;
}
const emit = defineEmits<AppAutosuggestDropdownEmits>();

const containerRef = ref<HTMLElement>();
const dropdownRef = ref<HTMLElement>();
const shouldOpenAbove = ref(false);

function selectItem(item: AutoCompleteSuggestion): void {
  emit('select', item);
}

function handleKeyDown(event: KeyboardEvent): void {
  if (!props.isOpen || props.suggestions.length === 0) {
    return;
  }

  switch (event.key) {
    // TODO: Handle arrow keys to move with arrows on the list
    case KeyboardKeyCode.Escape: {
      event.preventDefault();
      emit('close');
      break;
    }
  }
}

const ITEM_HEIGHT_PX = 44;
const dropdownContentStyle = computed(() => {
  if (!props.maxVisibleItems || props.maxVisibleItems <= 0) {
    return {};
  }
  return { maxHeight: `${props.maxVisibleItems * ITEM_HEIGHT_PX}px` };
});

function handleOverlayClick(): void {
  emit('close');
}

function checkPosition(): void {
  if (containerRef.value && dropdownRef.value && props.wrapperBottomBoundary) {
    const containerRect = containerRef.value.getBoundingClientRect();
    const dropdownHeight = dropdownRef.value.offsetHeight;

    const spaceBelow = props.wrapperBottomBoundary - containerRect.bottom;
    const newValue = spaceBelow < dropdownHeight;

    if (shouldOpenAbove.value !== newValue) {
      shouldOpenAbove.value = newValue;
      emit('update-should-open-above', newValue);
    }
  }
}

function getIconName(categoryType: CategoryType): AppSvgIconType {
  if (categoryType === CategoryType.Hotel) {
    return 'bedOutlined';
  } else if (categoryType === CategoryType.GeolocationPosition) {
    return 'position';
  }
  return 'locationMarkOutlined';
}

const shouldCheckPosition = computed(
  () => props.isOpen && props.suggestions.length
);

watch(shouldCheckPosition, (shouldCheck) => {
  if (shouldCheck) {
    nextTick(() => {
      checkPosition();
    });
  }
});

onMounted(() => {
  window.addEventListener('resize', checkPosition);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkPosition);
});

useEventListener(containerRef, 'keydown', handleKeyDown);
</script>

<template>
  <div ref="containerRef" class="app-autosuggest-container">
    <slot name="input" />

    <div
      v-if="isOpen && suggestions.length > 0"
      class="app-autosuggest-overlay"
      role="button"
      tabindex="0"
      aria-label="Close dropdown"
      @click.stop="handleOverlayClick"
      @keydown.enter="handleOverlayClick"
    />

    <AppFadeTransition animation-time="medium">
      <div
        v-if="isOpen && suggestions.length > 0"
        ref="dropdownRef"
        class="app-autosuggest-dropdown"
        :class="{ 'open-above': shouldOpenAbove }"
      >
        <div v-for="suggestion in suggestions" :key="suggestion.label">
          <div
            class="dropdown-header"
            :class="{ 'header-icon': props.headerIcon }"
          >
            <AppSvgIcon
              v-if="props.headerIcon"
              :name="getIconName(suggestion.type)"
              size="18"
            />
            {{ suggestion.label }}
          </div>
          <div class="dropdown-content" :style="dropdownContentStyle">
            <div
              v-for="(item, index) in suggestion.data"
              :key="index"
              class="dropdown-item"
              :class="{
                selected: item.label === selected,
                'has-icon': props.headerIcon,
              }"
              :aria-selected="item.label === selected"
              tabindex="0"
              role="option"
              @click="selectItem(item)"
              @keydown.enter.prevent="selectItem(item)"
              @keydown.space.prevent="selectItem(item)"
            >
              <div v-if="highlight" class="highlight-item">
                <AppHighlighter
                  :text="item.label"
                  :search-text="selected"
                  size="large"
                />
              </div>
              <div v-else>
                {{ item.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppFadeTransition>
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';
@import '../../styles/_media.scss';

.app-autosuggest-container {
  position: relative;
  width: 100%;
  .dropdown-header {
    display: flex;
    align-items: center;
    gap: var(--spacing-8);
    @include font-body-l-bold;
    color: var(--color-neutral-70);
    padding: calc(var(--spacing-20) / 2) var(--spacing-16);
    &.header-icon {
      padding-left: var(--spacing-16);
    }
  }
  .app-autosuggest-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
    z-index: var(--modal-overlay-z-index);
  }
  .app-autosuggest-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: -webkit-fill-available;
    z-index: var(--modal-z-index);
    background-color: var(--color-white);
    border-radius: 0 0 var(--border-radius-8) var(--border-radius-8);
    overflow: hidden;
    padding-top: var(--spacing-12);
    border: 1px solid var(--color-scrollbar);

    &.open-above {
      top: auto;
      bottom: 100%;
      border-radius: var(--border-radius-8) var(--border-radius-8) 0 0;
      padding-top: 0;
      padding-bottom: var(--spacing-12);

      .dropdown-content {
        flex-direction: column-reverse;

        .dropdown-item:last-child {
          padding-bottom: calc(var(--spacing-20) / 2);
        }

        .dropdown-item:first-child {
          padding-top: var(--spacing-12);
        }
      }
    }

    .dropdown-content {
      max-height: 240px;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      @include font-body-l-light;
      color: var(--color-neutral-70);

      .dropdown-item {
        display: flex;
        cursor: pointer;
        position: relative;
        padding: calc(var(--spacing-20) / 2) var(--spacing-16);
        &.has-icon {
          padding: calc(var(--spacing-20) / 2) var(--spacing-40);
        }
        @include on-hover {
          background-color: var(--color-neutral-10);
          outline: none;
        }

        &.selected {
          background-color: var(--color-neutral-10);
        }
      }

      .dropdown-item:last-child {
        padding-bottom: var(--spacing-12);
      }
    }
  }
}
::-webkit-scrollbar {
  display: none;
}
</style>
